import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import { Tab, Tabs } from '../../ui/Tabs';
import Alert from '../../ui/Alert';
import FeatureDistribution from '../FeatureDistribution';
import FeatureHeatmap from '../FeatureHeatmap';
import { getColorScale } from '../../../core/doc_util';
import { isExPecto } from '../../../core/util';
import { setSelectedFeatureScoreType } from '../../../actions/DeepSEAActions';
import styles from './VariantFeatureBrowser.css';
import GenomeBrowser from '../GenomeBrowser';

const defaultScoreRange = [-3, 0, 3];
const threeColorRange = ['#333fe8', '#ffffff', '#e34a33'];
const twoColorRange = ['#333fe8', '#ffffff'];
const maxColorMultiplier = 5;

const DEFAULT_LABELS = {
  title: 'Molecular-level biochemical effects prediction',
  legendText: 'Biochemical feature score',
  placeHolderText: 'Search features',
  featureTabTitle: 'Features',
};

const VariantFeatureBrowser = props => {
  const { models, sources, onFeatureInfo, variantData, genome } = props;
  const [divRef, { width }] = useMeasure();
  const [featureScoreCutoffs, setFeatureScoreCutoffs] = useState({});
  const [tabKey, setTabKey] = useState(1);
  const [colorScale, setColorScale] = useState(null);
  const dispatch = useDispatch();
  const selectedFeatureScoreType = useSelector(
    state => state.deepsea.selectedFeatureScoreType,
  );
  const warning = useSelector(state => state.genome.warning);
  const isExPectoJob = useSelector(state => state.deepsea.selectedJob.expecto);

  const containerWidth = width > 0 ? width : 1170;

  useEffect(() => {
    let selectedModelIdx = 0;
    const initialFeatureScoreCutoffs = {};
    models.forEach((model, idx) => {
      if (isExPecto(model)) selectedModelIdx = idx;
      if (model.defaultCutoff && model.track) {
        initialFeatureScoreCutoffs[model.track.name] = model.defaultCutoff;
      }
    });
    setFeatureScoreCutoffs(initialFeatureScoreCutoffs);
    dispatch(setSelectedFeatureScoreType(models[selectedModelIdx]));
  }, [models, dispatch]);

  const getThresholds = useCallback(
    (model, featureScoreCutoffs) =>
      model &&
      model.track && {
        [model.track.name]: featureScoreCutoffs[model.track.name],
      },
    [],
  );

  useEffect(() => {
    setColorScale(() =>
      getColorScale(
        selectedFeatureScoreType.range || defaultScoreRange,
        selectedFeatureScoreType &&
          selectedFeatureScoreType.range &&
          selectedFeatureScoreType.range.length > 2
          ? threeColorRange
          : twoColorRange,
      ),
    );
  }, [selectedFeatureScoreType]);

  const getBoundedColor = useCallback(
    value => {
      if (!colorScale) return null; // Return early if colorScale is not yet defined
      const scoreRange =
        (selectedFeatureScoreType && selectedFeatureScoreType.range) ||
        defaultScoreRange;
      const color =
        selectedFeatureScoreType.color ||
        getColorScale(
          scoreRange,
          selectedFeatureScoreType &&
            selectedFeatureScoreType.range &&
            selectedFeatureScoreType.range.length > 2
            ? threeColorRange
            : twoColorRange,
        );
      const minValue = scoreRange[0] * maxColorMultiplier;
      const maxValue = scoreRange[scoreRange.length - 1] * maxColorMultiplier;
      const boundedValue = Math.min(maxValue, Math.max(minValue, value));
      return color(boundedValue);
    },
    [colorScale, selectedFeatureScoreType],
  );

  const browserWarningMessage = variant => (
    <Alert style={{ width: 750, margin: 'auto', textAlign: 'center' }}>
      {`Multiple variants at ${variant.chromosome}:${variant.position} submitted.
      Genome track below displays only the top scoring variant at that position.`}
    </Alert>
  );

  const featureScoreOptions = models.map(model => ({
    ...model,
    label:
      isExPecto(model) || !isExPectoJob
        ? model.label
        : `DeepSEA ${model.label}`,
    value: model.name,
  }));

  const thresholds = getThresholds(
    selectedFeatureScoreType,
    featureScoreCutoffs,
  );
  const trackFeatureInSync = 'track' in models[0];

  const labels = {
    title: selectedFeatureScoreType.category || DEFAULT_LABELS.title,
    legendText: selectedFeatureScoreType.label || DEFAULT_LABELS.legendText,
    placeHolderText: selectedFeatureScoreType.type
      ? `Search ${selectedFeatureScoreType.type}`
      : DEFAULT_LABELS.placeHolderText,
    featureTabTitle:
      selectedFeatureScoreType.type || DEFAULT_LABELS.featureTabTitle,
  };
  labels.featureTabTitle =
    labels.featureTabTitle.charAt(0).toUpperCase() +
    labels.featureTabTitle.slice(1);

  return (
    <div ref={divRef} className={styles.container}>
      {warning && browserWarningMessage(warning)}
      <GenomeBrowser
        sources={sources}
        thresholds={thresholds}
        onTrackSelect={trackName => {
          if (trackFeatureInSync) {
            const model = models.find(m => m.track.name === trackName);
            if (model) dispatch(setSelectedFeatureScoreType(model));
          }
        }}
        onFeatureInfo={onFeatureInfo}
        genome={genome}
      />
      <Tabs
        style={{ marginTop: 20 }}
        defaultActiveKey={1}
        activeKey={tabKey}
        onSelect={k => {
          setTabKey(k);
        }}
        mountOnEnter
        className="tab-under"
        id="deepsea-tabs"
        bsStyle="pills"
      >
        <Tab eventKey={1} title="Heatmap">
          <FeatureHeatmap
            featureScoreOptions={featureScoreOptions}
            defaultFeatureScoreCutoffs={featureScoreCutoffs}
            setDefaultFeatureScoreCutoffs={newFeatureScoreCutoffs =>
              setFeatureScoreCutoffs(newFeatureScoreCutoffs)
            }
            color={getBoundedColor}
            containerWidth={containerWidth}
            variantData={variantData}
            heatmapLabels={labels}
          />
        </Tab>
        <Tab eventKey={2} title={labels.featureTabTitle} unmountOnExit>
          <FeatureDistribution
            featureScoreOptions={featureScoreOptions}
            color={getBoundedColor}
            title={labels.title}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

VariantFeatureBrowser.propTypes = {
  models: PropTypes.arrayOf(PropTypes.object),
  sources: PropTypes.arrayOf(PropTypes.object),
  onFeatureInfo: PropTypes.func,
  variantData: PropTypes.arrayOf(PropTypes.object),
  genome: PropTypes.string,
};

VariantFeatureBrowser.defaultProps = {
  sources: [],
  models: [],
  onFeatureInfo: null,
  variantData: null,
  genome: 'hg19',
};

export default withStyles(styles)(VariantFeatureBrowser);
